import React, { useState } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import './ImageUpload.css';

function ImageUpload({ onImageUpload, currentImage, user }) {
	const [uploading, setUploading] = useState(false);
	const [error, setError] = useState('');
	const [dragOver, setDragOver] = useState(false);

	const handleFileChange = async (file) => {
		if (!file) return;

		// Validate file type
		if (!file.type.startsWith('image/')) {
			setError('Please select an image file');
			return;
		}

		// Validate file size (max 5MB)
		if (file.size > 5 * 1024 * 1024) {
			setError('Image size should be less than 5MB');
			return;
		}

		setUploading(true);
		setError('');

		try {
			// Upload to server
			const formData = new FormData();
			formData.append('file', file);

			// Get token from Redux store via connect
			const token = user?.token || localStorage.getItem('token');
			console.log('Uploading with token:', token);

			const response = await axios.post('/admin/upload/image', formData, {
				headers: {
					'Content-Type': 'multipart/form-data',
					Authorization: `Bearer ${token}`
				}
			});

			if (response.data.status && response.data.data.url) {
				onImageUpload(response.data.data.url);
			} else {
				setError('Failed to upload image');
			}
			setUploading(false);
		} catch (err) {
			setError(err.response?.data?.message || 'Failed to upload image');
			setUploading(false);
		}
	};

	const handleInputChange = (e) => {
		handleFileChange(e.target.files[0]);
	};

	const handleDrop = (e) => {
		e.preventDefault();
		setDragOver(false);
		const file = e.dataTransfer.files[0];
		handleFileChange(file);
	};

	const handleDragOver = (e) => {
		e.preventDefault();
		setDragOver(true);
	};

	const handleDragLeave = (e) => {
		e.preventDefault();
		setDragOver(false);
	};

	const handleRemoveImage = () => {
		onImageUpload('');
	};

	return (
		<div className="image-upload">
			<label className="form-label">Thumbnail Image</label>
			
			{error && (
				<div className="upload-error">
					<span className="error-icon">⚠️</span>
					{error}
					<button className="error-close" onClick={() => setError('')}>×</button>
				</div>
			)}
			
			{currentImage ? (
				<div className="current-image">
					<div className="image-preview">
						<img 
							src={currentImage} 
							alt="Thumbnail preview"
						/>
						<div className="image-overlay">
							<div className="d-flex gap-2">
								<label htmlFor="thumbnail-change" className="btn btn-change">
									🔄 Change
								</label>
								<button 
									className="btn btn-remove"
									onClick={handleRemoveImage}
									type="button"
								>
									🗑️ Remove
								</button>
							</div>
							<input
								type="file"
								accept="image/*"
								onChange={handleInputChange}
								disabled={uploading}
								className="file-input"
								id="thumbnail-change"
							/>
						</div>
					</div>
				</div>
			) : (
				<div 
					className={`upload-area ${dragOver ? 'drag-over' : ''}`}
					onDrop={handleDrop}
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
				>
					<div className="upload-content">
						<div className="upload-icon">📸</div>
						<h4>Upload Thumbnail</h4>
						<p>Drag & drop an image here, or click to select</p>
						<input
							type="file"
							accept="image/*"
							onChange={handleInputChange}
							disabled={uploading}
							className="file-input"
							id="thumbnail-upload"
						/>
						<label htmlFor="thumbnail-upload" className="upload-button">
							{uploading ? 'Uploading...' : 'Choose Image'}
						</label>
						<div className="upload-info">
							<small>Supports: JPG, PNG, GIF (Max 5MB)</small>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

const mapStateToProps = (state) => ({
	user: state.user
});

export default connect(mapStateToProps)(ImageUpload);
